import { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle, RefreshCw, Bell, XCircle } from 'lucide-react';
import { supabase } from '../integrations/supabase/client';
import { toast } from 'sonner';

export function Alerts() {
  const queryClient = useQueryClient();
 
// eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [alerts, setAlerts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAlerts();
  }, []);

  const fetchAlerts = async () => {
    setLoading(true);
    try {
      const { data } = await supabase
        .from('system_alerts')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(50);
      setAlerts(data || []);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const resolveAlert = async (id: string) => {
    try {
      await supabase
        .from('system_alerts')
        .update({ is_resolved: true, resolved_at: new Date().toISOString() })
        .eq('id', id);
      setAlerts(prev => prev.map(a => a.id === id ? { ...a, is_resolved: true } : a));
      toast.success('Alerta resolvido');
    } catch (error) {
      toast.error('Erro ao resolver alerta');
    }
  };

  const handleResolveUsageAlert = async (alertId: string) => {
    try {
      const { error } = await supabase
        .from('usage_alerts')
        .update({ status: 'resolved', resolved_at: new Date().toISOString() })
        .eq('id', alertId);
      
      if (error) throw error;
      
      toast.success('Alerta de uso resolvido');
      queryClient.invalidateQueries({ queryKey: ['usage-alerts'] });
    } catch (error) {
      console.error('Erro ao resolver alerta de uso:', error);
      toast.error('Erro ao resolver alerta');
    }
  };

  const handleDismissUsageAlert = async (alertId: string) => {
    try {
      const { error } = await supabase
        .from('usage_alerts')
        .update({ status: 'dismissed' })
        .eq('id', alertId);
      
      if (error) throw error;
      
      toast.success('Alerta de uso dispensado');
      queryClient.invalidateQueries({ queryKey: ['usage-alerts'] });
    } catch (error) {
      console.error('Erro ao dispensar alerta de uso:', error);
      toast.error('Erro ao dispensar alerta');
    }
  };

  const severityColors: Record<string, string> = {
    info: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    warning: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    error: 'bg-red-500/20 text-red-400 border-red-500/30',
    critical: 'bg-red-600/30 text-red-300 border-red-600/50',
  };

  const activeAlerts = alerts.filter(a => !a.is_resolved);

  // Buscar alertas de uso
  const { data: usageAlerts, isLoading: usageLoading } = useQuery({
    queryKey: ['usage-alerts'],
    queryFn: async () => {
      const { data: alertsData, error: alertsError } = await supabase
        .from('usage_alerts')
        .select('*')
        .eq('status', 'active')
        .order('created_at', { ascending: false })
      
      if (alertsError) throw alertsError
      
      // Buscar emails dos usuários via Edge Function
      const userIds = [...new Set(alertsData?.map(a => a.user_id) || [])]
      const userEmailMap = new Map<string, string>()
      
      try {
        const { data: usersData, error: usersError } = await supabase.functions.invoke('admin-list-users')
 
        if (!usersError && usersData?.users) {
// eslint-disable-next-line @typescript-eslint/no-explicit-any
          usersData.users.forEach((u: any) => {
            if (userIds.includes(u.id)) {
              userEmailMap.set(u.id, u.email || 'N/A')
            }
          })
        }
      } catch (err) {
        console.error('Erro ao buscar emails:', err)
        // Fallback: buscar via asaas_customers se disponível
        if (userIds.length > 0) {
          const { data: customers } = await supabase
            .from('asaas_customers')
            .select('user_id, email')
            .in('user_id', userIds)
          
          if (customers) {
            customers.forEach(c => {
              userEmailMap.set(c.user_id, c.email)
            })
          }
        }
      }
      
      return alertsData?.map(alert => ({
        ...alert,
        user_email: userEmailMap.get(alert.user_id) || 'Usuário desconhecido'
      })) || []
    },
  })

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Alertas do Sistema</h1>
          <p className="text-slate-400">{activeAlerts.length} alertas ativos</p>
        </div>
        <button
          onClick={fetchAlerts}
          className="flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-slate-300 hover:bg-white/10"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Atualizar
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className={`rounded-2xl p-6 border ${activeAlerts.length > 0 ? 'bg-red-500/10 border-red-500/30' : 'bg-[#0a0a0a] border-white/10'}`}>
          <AlertTriangle className={`w-8 h-8 mb-3 ${activeAlerts.length > 0 ? 'text-red-400' : 'text-slate-400'}`} />
          <p className="text-sm text-slate-400">Alertas Ativos</p>
          <p className="text-3xl font-bold text-white">{activeAlerts.length}</p>
        </div>
        <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6">
          <Bell className="w-8 h-8 text-amber-400 mb-3" />
          <p className="text-sm text-slate-400">Warnings</p>
          <p className="text-3xl font-bold text-white">
            {alerts.filter(a => a.severity === 'warning' && !a.is_resolved).length}
          </p>
        </div>
        <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6">
          <XCircle className="w-8 h-8 text-red-400 mb-3" />
          <p className="text-sm text-slate-400">Críticos</p>
          <p className="text-3xl font-bold text-white">
            {alerts.filter(a => a.severity === 'critical' && !a.is_resolved).length}
          </p>
        </div>
        <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6">
          <CheckCircle className="w-8 h-8 text-emerald-400 mb-3" />
          <p className="text-sm text-slate-400">Resolvidos Hoje</p>
          <p className="text-3xl font-bold text-white">
            {alerts.filter(a => a.is_resolved).length}
          </p>
        </div>
      </div>

      <div className="space-y-4">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <RefreshCw className="w-8 h-8 animate-spin text-red-500" />
          </div>
        ) : alerts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-slate-400 bg-[#0a0a0a] border border-white/10 rounded-2xl">
            <CheckCircle className="w-12 h-12 mb-4 text-emerald-400" />
            <p>Nenhum alerta no sistema</p>
          </div>
        ) : (
          alerts.map((alert) => (
            <div
              key={alert.id}
              className={`p-6 rounded-2xl border ${severityColors[alert.severity]} ${alert.is_resolved === true ? 'opacity-50' : ''}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <AlertTriangle className="w-5 h-5 mt-0.5" />
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-white">{alert.title}</h3>
                      {alert.is_resolved ? (
                        <span className="px-2 py-1 bg-green-500/10 text-green-400 rounded text-xs">Resolvido</span>
                      ) : (
                        <span className="px-2 py-1 bg-red-500/10 text-red-400 rounded text-xs">Ativo</span>
                      )}
                    </div>
                    <p className="text-sm text-slate-400 mt-1">{alert.message}</p>
                    <p className="text-xs text-slate-500 mt-2">
                      {new Date(alert.created_at).toLocaleString('pt-BR')}
                    </p>
                  </div>
                </div>
                {!alert.is_resolved && (
                  <button
                    onClick={() => resolveAlert(alert.id)}
                    className="px-3 py-1.5 bg-white/10 rounded-lg text-sm text-white hover:bg-white/20 transition-colors"
                  >
                    Resolver
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Alertas de Uso */}
      <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-6">
          <Bell className="w-5 h-5 text-amber-400" />
          <h3 className="text-lg font-semibold text-white">Alertas de Uso</h3>
        </div>
        <p className="text-sm text-slate-400 mb-4">
          Clientes próximos do limite do plano
        </p>
        {usageLoading ? (
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="w-8 h-8 animate-spin text-red-500" />
          </div>
        ) : usageAlerts && usageAlerts.length > 0 ? (
          <div className="space-y-4">
            {usageAlerts.map((alert) => (
              <div key={alert.id} className="flex items-center justify-between p-4 border border-white/10 rounded-lg bg-white/5">
                <div className="flex-1">
                  <p className="font-medium text-white">{alert.user_email}</p>
                  <p className="text-sm text-slate-400 mt-1">
                    {alert.alert_type === 'messages' ? 'Mensagens' : alert.alert_type === 'ai_responses' ? 'Respostas IA' : 'Contatos'}:{' '}
                    <span className="font-semibold text-white">{alert.threshold_percentage}%</span> do limite
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    {alert.current_usage.toLocaleString()} / {alert.limit_value.toLocaleString()}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    alert.threshold_percentage >= 95 
                      ? 'bg-red-500/20 text-red-400' 
                      : 'bg-amber-500/20 text-amber-400'
                  }`}>
                    {alert.threshold_percentage}%
                  </span>
                  <button
                    onClick={() => handleResolveUsageAlert(alert.id)}
                    className="px-3 py-1.5 bg-white/10 rounded-lg text-sm text-white hover:bg-white/20 transition-colors flex items-center gap-1"
                    title="Resolver alerta"
                  >
                    <CheckCircle className="w-4 h-4" />
                    Resolver
                  </button>
                  <button
                    onClick={() => handleDismissUsageAlert(alert.id)}
                    className="px-3 py-1.5 bg-white/10 rounded-lg text-sm text-white hover:bg-white/20 transition-colors"
                    title="Dispensar alerta"
                  >
                    <XCircle className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-slate-400">
            Nenhum alerta de uso ativo
          </div>
        )}
      </div>
    </div>
  );
}

